import React from 'react';
import {
  saBillingCanManage,
  saBillingFormatBillingCycle,
  saBillingFormatDateBR,
  saBillingFormatDateTimeBR,
  saBillingFormatError,
  saBillingFormatMoneyBRL,
  saBillingFormatPaymentMethod,
  saBillingFormatSubscriptionStatus,
  saBillingMapGlobalSubscription,
} from './billing-support.jsx';
import { ApiErrorState, billingService, tenantService, useGlobalBillingSubscriptions } from '../services.jsx';
// Billing subscriptions — lista global, detalhe tenant-scoped e gestão (F3).
// Sem import/export. Sem HTTP direto. Sem cálculo financeiro.
  const { useState, useEffect, useRef, useMemo } = React;

  const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function getFocusableElements(container) {
    if (!container) return [];
    const nodes = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
    return nodes.filter((el) => {
      if (!el || el.disabled) return false;
      const style = window.getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none') return false;
      if (el.offsetParent === null && style.position !== 'fixed') return false;
      return true;
    });
  }

  function trapFocus(event, container) {
    if (!container || event.key !== 'Tab') return;
    const focusable = getFocusableElements(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const SUBSCRIPTION_FIELD_IDS = {
    tenant_id: 'billing-subscription-tenant-search',
    amount: 'billing-subscription-amount',
    billing_cycle: 'billing-subscription-cycle',
    payment_method: 'billing-subscription-payment-method',
    start_date: 'billing-subscription-start-date',
    next_due_date: 'billing-subscription-next-due-date',
    notes: 'billing-subscription-notes',
    reason: 'billing-subscription-reason',
  };

  const SUBSCRIPTION_ERROR_FIELD_ORDER = [
    'tenant_id', 'amount', 'billing_cycle', 'payment_method',
    'start_date', 'next_due_date', 'notes', 'reason',
  ];

  function focusFirstFormError(errors) {
    if (!errors || typeof errors !== 'object') return;
    for (let i = 0; i < SUBSCRIPTION_ERROR_FIELD_ORDER.length; i += 1) {
      const key = SUBSCRIPTION_ERROR_FIELD_ORDER[i];
      if (errors[key]) {
        const el = document.getElementById(SUBSCRIPTION_FIELD_IDS[key]);
        if (el && typeof el.focus === 'function') {
          el.focus();
          return;
        }
      }
    }
  }

  const ACCEPTED_QUERY_KEYS = [
    'tenant_id',
    'status',
    'tenant_status',
    'plan_code',
    'billing_cycle',
    'payment_method',
    'next_due_date_from',
    'next_due_date_to',
    'created_from',
    'created_to',
    'search',
    'ordering',
    'page',
    'page_size',
  ];

  const TENANT_STATUS_LABELS = {
    active: 'Ativo',
    suspended: 'Suspenso',
    archived: 'Arquivado',
  };

  const HISTORY_EVENT_LABELS = {
    'billing.subscription.created': 'Assinatura criada',
    'billing.subscription.updated': 'Assinatura atualizada',
    'billing.subscription.ended': 'Assinatura encerrada',
    'billing.subscription.cancelled': 'Assinatura cancelada',
  };

  const BILLING_CYCLES = ['monthly', 'annual'];
  const PAYMENT_METHODS = [
    'not_informed',
    'manual',
    'pix',
    'bank_transfer',
    'boleto',
    'card',
    'other',
  ];
  const SUBSCRIPTION_STATUSES = ['active', 'ended', 'cancelled'];
  const TENANT_STATUSES = ['active', 'suspended', 'archived'];
  const ORDERING_OPTIONS = [
    { value: '-created_at', label: 'Criação (mais recente)' },
    { value: 'created_at', label: 'Criação (mais antiga)' },
    { value: 'next_due_date', label: 'Vencimento (mais próximo)' },
    { value: '-next_due_date', label: 'Vencimento (mais distante)' },
    { value: '-amount', label: 'Valor (maior)' },
    { value: 'amount', label: 'Valor (menor)' },
    { value: 'tenant_name', label: 'Tenant (A–Z)' },
    { value: '-tenant_name', label: 'Tenant (Z–A)' },
    { value: 'status', label: 'Status (A–Z)' },
    { value: '-status', label: 'Status (Z–A)' },
  ];

  const DEFAULT_LIST_STATE = {
    search: '',
    status: '',
    tenant_status: '',
    plan_code: '',
    billing_cycle: '',
    payment_method: '',
    next_due_date_from: '',
    next_due_date_to: '',
    created_from: '',
    created_to: '',
    ordering: '-created_at',
    page: 1,
    page_size: 20,
  };

  function money(value) {
    if (typeof saBillingFormatMoneyBRL === 'function') {
      return saBillingFormatMoneyBRL(value);
    }
    return '—';
  }

  function dateBR(value) {
    if (typeof saBillingFormatDateBR === 'function') {
      return saBillingFormatDateBR(value);
    }
    return '—';
  }

  function dateTimeBR(value) {
    if (typeof saBillingFormatDateTimeBR === 'function') {
      return saBillingFormatDateTimeBR(value);
    }
    return '—';
  }

  function formatCycle(code) {
    if (typeof saBillingFormatBillingCycle === 'function') {
      return saBillingFormatBillingCycle(code);
    }
    return code || '—';
  }

  function formatPayment(code) {
    if (typeof saBillingFormatPaymentMethod === 'function') {
      return saBillingFormatPaymentMethod(code);
    }
    return code || '—';
  }

  function formatStatus(code) {
    if (typeof saBillingFormatSubscriptionStatus === 'function') {
      return saBillingFormatSubscriptionStatus(code);
    }
    return code || '—';
  }

  function formatError(error, fallback) {
    if (typeof saBillingFormatError === 'function') {
      return saBillingFormatError(error, fallback);
    }
    return (error && error.message) || fallback;
  }

  function todayLocalISO() {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const d = now.getDate();
    const mm = m < 10 ? `0${m}` : String(m);
    const dd = d < 10 ? `0${d}` : String(d);
    return `${y}-${mm}-${dd}`;
  }

  function isPositiveIntegerId(value) {
    if (value == null || value === '') return false;
    if (typeof value === 'number') {
      return Number.isInteger(value) && value > 0;
    }
    const str = String(value).trim();
    if (!/^\d+$/.test(str)) return false;
    const num = Number(str);
    return Number.isInteger(num) && num > 0;
  }

  function billingEntityKey(tenantId, entityId) {
    return `${tenantId}:${entityId}`;
  }

  function isValidDateOnly(value) {
    if (value === null || value === undefined || value === '') return false;
    const str = String(value).trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!match) return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const local = new Date(year, month - 1, day);
    return (
      local.getFullYear() === year
      && local.getMonth() === month - 1
      && local.getDate() === day
    );
  }

  function actorLabel(actor) {
    if (!actor || typeof actor !== 'object') return '—';
    if (actor.name) return actor.name;
    if (actor.email) return actor.email;
    if (actor.id != null) return `Usuário #${actor.id}`;
    return '—';
  }

  function snapshotDiffEntries(before, after) {
    const a = before && typeof before === 'object' ? before : {};
    const b = after && typeof after === 'object' ? after : {};
    const keys = Array.from(new Set([].concat(Object.keys(a), Object.keys(b))));
    return keys
      .filter((key) => String(a[key] ?? '') !== String(b[key] ?? ''))
      .map((key) => ({
        key,
        before: a[key] == null || a[key] === '' ? '—' : String(a[key]),
        after: b[key] == null || b[key] === '' ? '—' : String(b[key]),
      }));
  }

  function saBillingBuildSubscriptionQuery(state) {
    const source = state && typeof state === 'object' ? state : {};
    const out = {};

    ACCEPTED_QUERY_KEYS.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(source, key)) return;
      let value = source[key];
      if (value === null || value === undefined) return;

      if (key === 'search') {
        const trimmed = String(value).trim().slice(0, 200);
        if (trimmed) out.search = trimmed;
        return;
      }

      if (key === 'plan_code') {
        const trimmed = String(value).trim();
        if (trimmed && trimmed !== 'all') out.plan_code = trimmed;
        return;
      }

      if (key === 'page') {
        const page = Math.max(1, Math.floor(Number(value)) || 1);
        out.page = page;
        return;
      }

      if (key === 'page_size') {
        let size = Math.floor(Number(value)) || 20;
        if (size < 1) size = 1;
        if (size > 100) size = 100;
        out.page_size = size;
        return;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed || trimmed === 'all') return;
        out[key] = trimmed;
        return;
      }

      out[key] = value;
    });

    if (out.page == null) out.page = 1;
    if (out.page_size == null) out.page_size = 20;
    if (out.ordering == null) out.ordering = '-created_at';

    return out;
  }

  function saBillingBuildSubscriptionsViewModel(response) {
    const payload = response && typeof response === 'object' ? response : {};
    const results = Array.isArray(payload.results) ? payload.results : [];
    const count = Number(payload.count) || 0;
    const page = Number(payload.page) || 1;
    const pageSize = Number(payload.page_size) || 20;

    const rows = results.map((item) => {
      const mapped = typeof saBillingMapGlobalSubscription === 'function'
        ? saBillingMapGlobalSubscription(item)
        : Object.assign({}, item || {});

      const tenantName = mapped.tenant_name || '—';
      const tenantSchema = mapped.tenant_schema || '';
      const planName = mapped.plan_name_snapshot || mapped.plan_code_snapshot || '—';
      const planCode = mapped.plan_code_snapshot || '';
      const inSync = mapped.plan_in_sync === true;

      return Object.assign({}, mapped, {
        tenant_label: tenantSchema ? `${tenantName} (${tenantSchema})` : tenantName,
        tenant_status_label: TENANT_STATUS_LABELS[mapped.tenant_status] || mapped.tenant_status || '—',
        plan_label: planName,
        plan_code_label: planCode && planCode !== planName ? planCode : '',
        sync_label: inSync ? 'Sincronizado' : 'Divergente',
        sync_tone: inSync ? 'ok' : 'warning',
        status_label: mapped.status_label || formatStatus(mapped.status),
        amount_formatted: mapped.amount_formatted || money(mapped.amount),
        monthly_equivalent_formatted:
          mapped.monthly_equivalent_formatted || money(mapped.monthly_equivalent),
        billing_cycle_label: mapped.billing_cycle_label || formatCycle(mapped.billing_cycle),
        payment_method_label: mapped.payment_method_label || formatPayment(mapped.payment_method),
        start_date_formatted: dateBR(mapped.start_date),
        next_due_date_formatted:
          mapped.next_due_date_formatted || dateBR(mapped.next_due_date),
        created_at_formatted: dateTimeBR(mapped.created_at),
        updated_at_formatted: mapped.updated_at_formatted || dateTimeBR(mapped.updated_at),
      });
    });

    return {
      rows,
      count,
      page,
      page_size: pageSize,
      total_pages: Math.max(1, Math.ceil(count / pageSize)),
      is_empty: count === 0,
    };
  }

  function saBillingValidateSubscriptionForm(form, mode) {
    const source = form && typeof form === 'object' ? form : {};
    const errors = {};
    const isCreate = mode === 'create';

    if (isCreate) {
      if (!isPositiveIntegerId(source.tenant_id)) {
        errors.tenant_id = 'Selecione um tenant.';
      }
    }

    const amountRaw = source.amount == null ? '' : String(source.amount).trim();
    if (!amountRaw) {
      errors.amount = 'Informe o valor.';
    } else if (/,/.test(amountRaw) || !/^\d+(\.\d{1,2})?$/.test(amountRaw)) {
      errors.amount = 'Use decimal positivo com ponto (ex.: 199.90).';
    } else {
      const amountNum = Number(amountRaw);
      if (!Number.isFinite(amountNum) || amountNum <= 0) {
        errors.amount = 'O valor deve ser maior que zero.';
      }
    }

    const billingCycle = String(source.billing_cycle || '').trim();
    if (!BILLING_CYCLES.includes(billingCycle)) {
      errors.billing_cycle = 'Ciclo de cobrança inválido.';
    }

    const startDate = String(source.start_date || '').trim();
    if (!isValidDateOnly(startDate)) {
      errors.start_date = 'Data de início inválida.';
    }

    const nextDue = String(source.next_due_date || '').trim();
    if (!isValidDateOnly(nextDue)) {
      errors.next_due_date = 'Próximo vencimento inválido.';
    } else if (isValidDateOnly(startDate) && nextDue < startDate) {
      errors.next_due_date = 'O vencimento deve ser igual ou posterior ao início.';
    }

    const paymentMethod = String(source.payment_method || '').trim();
    if (!PAYMENT_METHODS.includes(paymentMethod)) {
      errors.payment_method = 'Forma de pagamento inválida.';
    }

    const notes = source.notes == null ? '' : String(source.notes);
    if (notes.length > 2000) {
      errors.notes = 'Observações: máximo de 2000 caracteres.';
    }

    const reason = source.reason == null ? '' : String(source.reason).trim();
    if (reason.length < 5 || reason.length > 500) {
      errors.reason = 'Motivo deve ter entre 5 e 500 caracteres.';
    }

    const valid = Object.keys(errors).length === 0;
    let payload = null;
    if (valid) {
      if (isCreate) {
        payload = {
          amount: amountRaw,
          currency: 'BRL',
          billing_cycle: billingCycle,
          start_date: startDate,
          next_due_date: nextDue,
          payment_method: paymentMethod,
          notes,
          reason,
        };
      } else {
        payload = {
          amount: amountRaw,
          billing_cycle: billingCycle,
          start_date: startDate,
          next_due_date: nextDue,
          payment_method: paymentMethod,
          notes,
          reason,
        };
      }
    }

    return { valid, errors, payload };
  }

  function validateDateRange(from, to) {
    if (!from && !to) return null;
    if (from && !isValidDateOnly(from)) return 'Data inicial inválida.';
    if (to && !isValidDateOnly(to)) return 'Data final inválida.';
    if (from && to && from > to) return 'A data inicial deve ser anterior ou igual à final.';
    return null;
  }

  function DetailField({ label, value }) {
    return (
      <div className="billing-subscription-detail-field">
        <div className="billing-subscription-detail-label">{label}</div>
        <div className="billing-subscription-detail-value">{value == null || value === '' ? '—' : value}</div>
      </div>
    );
  }

  function FieldError({ id, message }) {
    if (!message) return null;
    return (
      <div id={id} className="billing-subscription-field-error" role="alert">
        {message}
      </div>
    );
  }

  function SubscriptionFormFields({
    form,
    errors,
    onChange,
    mode,
    selectedTenant,
    tenantSearch,
    onTenantSearchChange,
    tenantResults,
    tenantLoading,
    onSelectTenant,
    onClearTenant,
  }) {
    const tenantResultsId = 'billing-subscription-tenant-results';
    const tenantStatusId = 'billing-subscription-tenant-search-status';
    return (
      <div className="billing-subscription-form-grid">
        {mode === 'create' && (
          <div className="billing-subscription-form-span">
            <label htmlFor="billing-subscription-tenant-search">Tenant</label>
            {selectedTenant ? (
              <div className="billing-subscription-tenant-selected">
                <div>
                  <strong>{selectedTenant.name}</strong>
                  <div className="billing-subscription-muted">
                    {selectedTenant.schema_name}
                    {selectedTenant.plan && selectedTenant.plan.name
                      ? ` · Plano ${selectedTenant.plan.name}`
                      : selectedTenant.plan && selectedTenant.plan.id
                        ? ` · Plano ${selectedTenant.plan.id}`
                        : ''}
                    {selectedTenant.operational_status
                      ? ` · ${selectedTenant.operational_status}`
                      : ''}
                  </div>
                </div>
                <button type="button" className="btn" onClick={onClearTenant}>Trocar</button>
              </div>
            ) : (
              <>
                <input
                  id="billing-subscription-tenant-search"
                  type="search"
                  value={tenantSearch}
                  onChange={(e) => onTenantSearchChange(e.target.value)}
                  placeholder="Buscar tenant por nome ou schema"
                  maxLength={200}
                  aria-invalid={Boolean(errors.tenant_id)}
                  aria-describedby={
                    errors.tenant_id
                      ? 'billing-subscription-tenant-search-error'
                      : tenantStatusId
                  }
                  aria-controls={tenantResults.length ? tenantResultsId : undefined}
                  aria-busy={tenantLoading || undefined}
                />
                <div
                  id={tenantStatusId}
                  className="billing-subscription-muted"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {tenantLoading
                    ? 'Buscando tenants…'
                    : tenantResults.length > 0
                      ? `${tenantResults.length} resultado${tenantResults.length === 1 ? '' : 's'}`
                      : tenantSearch.trim() && !tenantLoading
                        ? 'Nenhum tenant encontrado.'
                        : ''}
                </div>
                {!tenantLoading && tenantResults.length > 0 && (
                  <ul
                    id={tenantResultsId}
                    className="billing-subscription-tenant-results"
                    aria-label="Resultados da busca de tenants"
                  >
                    {tenantResults.map((tenant) => (
                      <li key={tenant.tenant_id}>
                        <button
                          type="button"
                          onClick={() => onSelectTenant(tenant)}
                          aria-label={`Selecionar tenant ${tenant.name}, schema ${tenant.schema_name}${
                            tenant.plan && (tenant.plan.name || tenant.plan.id)
                              ? `, plano ${tenant.plan.name || tenant.plan.id}`
                              : ''
                          }${
                            tenant.operational_status
                              ? `, status ${tenant.operational_status}`
                              : ''
                          }`}
                        >
                          <strong>{tenant.name}</strong>
                          <span>
                            {tenant.schema_name}
                            {tenant.plan && (tenant.plan.name || tenant.plan.id)
                              ? ` · ${tenant.plan.name || tenant.plan.id}`
                              : ''}
                            {tenant.operational_status ? ` · ${tenant.operational_status}` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
            <FieldError id="billing-subscription-tenant-search-error" message={errors.tenant_id} />
          </div>
        )}

        <div>
          <label htmlFor="billing-subscription-amount">Valor (BRL)</label>
          <input
            id="billing-subscription-amount"
            type="text"
            inputMode="decimal"
            value={form.amount}
            onChange={(e) => onChange('amount', e.target.value)}
            placeholder="199.90"
            aria-invalid={Boolean(errors.amount)}
            aria-describedby={errors.amount ? 'billing-subscription-amount-error' : undefined}
          />
          <FieldError id="billing-subscription-amount-error" message={errors.amount} />
        </div>

        <div>
          <label htmlFor="billing-subscription-currency">Moeda</label>
          <input id="billing-subscription-currency" type="text" value="BRL" disabled readOnly />
        </div>

        <div>
          <label htmlFor="billing-subscription-cycle">Ciclo</label>
          <select
            id="billing-subscription-cycle"
            value={form.billing_cycle}
            onChange={(e) => onChange('billing_cycle', e.target.value)}
            aria-invalid={Boolean(errors.billing_cycle)}
            aria-describedby={errors.billing_cycle ? 'billing-subscription-cycle-error' : undefined}
          >
            {BILLING_CYCLES.map((cycle) => (
              <option key={cycle} value={cycle}>{formatCycle(cycle)}</option>
            ))}
          </select>
          <FieldError id="billing-subscription-cycle-error" message={errors.billing_cycle} />
        </div>

        <div>
          <label htmlFor="billing-subscription-payment-method">Forma de pagamento</label>
          <select
            id="billing-subscription-payment-method"
            value={form.payment_method}
            onChange={(e) => onChange('payment_method', e.target.value)}
            aria-invalid={Boolean(errors.payment_method)}
            aria-describedby={
              errors.payment_method ? 'billing-subscription-payment-method-error' : undefined
            }
          >
            {PAYMENT_METHODS.map((method) => (
              <option key={method} value={method}>{formatPayment(method)}</option>
            ))}
          </select>
          <FieldError
            id="billing-subscription-payment-method-error"
            message={errors.payment_method}
          />
        </div>

        <div>
          <label htmlFor="billing-subscription-start-date">Início</label>
          <input
            id="billing-subscription-start-date"
            type="date"
            value={form.start_date}
            onChange={(e) => onChange('start_date', e.target.value)}
            aria-invalid={Boolean(errors.start_date)}
            aria-describedby={errors.start_date ? 'billing-subscription-start-date-error' : undefined}
          />
          <FieldError id="billing-subscription-start-date-error" message={errors.start_date} />
        </div>

        <div>
          <label htmlFor="billing-subscription-next-due-date">Próximo vencimento</label>
          <input
            id="billing-subscription-next-due-date"
            type="date"
            value={form.next_due_date}
            onChange={(e) => onChange('next_due_date', e.target.value)}
            aria-invalid={Boolean(errors.next_due_date)}
            aria-describedby={
              errors.next_due_date ? 'billing-subscription-next-due-date-error' : undefined
            }
          />
          <FieldError
            id="billing-subscription-next-due-date-error"
            message={errors.next_due_date}
          />
        </div>

        <div className="billing-subscription-form-span">
          <label htmlFor="billing-subscription-notes">Observações</label>
          <textarea
            id="billing-subscription-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => onChange('notes', e.target.value)}
            maxLength={2000}
            aria-invalid={Boolean(errors.notes)}
            aria-describedby={errors.notes ? 'billing-subscription-notes-error' : undefined}
          />
          <FieldError id="billing-subscription-notes-error" message={errors.notes} />
        </div>

        <div className="billing-subscription-form-span">
          <label htmlFor="billing-subscription-reason">Motivo</label>
          <textarea
            id="billing-subscription-reason"
            rows={2}
            value={form.reason}
            onChange={(e) => onChange('reason', e.target.value)}
            maxLength={500}
            placeholder="Descreva o motivo da operação (5 a 500 caracteres)"
            aria-invalid={Boolean(errors.reason)}
            aria-describedby={errors.reason ? 'billing-subscription-reason-error' : undefined}
          />
          <FieldError id="billing-subscription-reason-error" message={errors.reason} />
        </div>
      </div>
    );
  }

  function SaBillingSubscriptions({ currentUser, onToast, onBillingChanged }) {
    const canManage = typeof saBillingCanManage === 'function'
      ? saBillingCanManage(currentUser)
      : false;

    const [listState, setListState] = useState(() => Object.assign({}, DEFAULT_LIST_STATE));
    const [searchInput, setSearchInput] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [drawerTab, setDrawerTab] = useState('detalhes');
    const [historyItems, setHistoryItems] = useState([]);
    const [historyMeta, setHistoryMeta] = useState({ count: 0, page: 1, page_size: 20 });
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [historyExpanded, setHistoryExpanded] = useState({});
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [tenantSearch, setTenantSearch] = useState('');
    const [tenantResults, setTenantResults] = useState([]);
    const [tenantLoading, setTenantLoading] = useState(false);
    const [apiParams, setApiParams] = useState(() => saBillingBuildSubscriptionQuery(DEFAULT_LIST_STATE));

    const drawerTriggerRef = useRef(null);
    const modalTriggerRef = useRef(null);
    const drawerCloseButtonRef = useRef(null);
    const modalInitialFocusRef = useRef(null);
    const drawerDialogRef = useRef(null);
    const modalDialogRef = useRef(null);
    const drawerTabRefs = useRef({});
    const detailRequestRef = useRef(0);
    const historyRequestRef = useRef(0);
    const activeEntityKeyRef = useRef(null);
    const tenantSearchRef = useRef(0);

    const dueRangeError = validateDateRange(
      listState.next_due_date_from,
      listState.next_due_date_to,
    );
    const createdRangeError = validateDateRange(listState.created_from, listState.created_to);
    const filtersInvalid = Boolean(dueRangeError || createdRangeError);

    useEffect(() => {
      if (filtersInvalid) return;
      setApiParams(saBillingBuildSubscriptionQuery(listState));
    }, [listState, filtersInvalid]);

    const {
      data,
      meta,
      loading,
      error,
      refetch,
    } = useGlobalBillingSubscriptions(apiParams);

    const vm = useMemo(() => saBillingBuildSubscriptionsViewModel({
      results: data,
      count: meta.count,
      page: meta.page,
      page_size: meta.page_size,
    }), [data, meta]);

    const hasActiveFilters = Boolean(
      listState.search
      || listState.status
      || listState.tenant_status
      || listState.plan_code
      || listState.billing_cycle
      || listState.payment_method
      || listState.next_due_date_from
      || listState.next_due_date_to
      || listState.created_from
      || listState.created_to,
    );

    useEffect(() => {
      const timer = setTimeout(() => {
        setListState((prev) => {
          const nextSearch = searchInput.trim().slice(0, 200);
          if (prev.search === nextSearch) return prev;
          return Object.assign({}, prev, { search: nextSearch, page: 1 });
        });
      }, 350);
      return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
      const hasOverlay = Boolean(selectedRow || modal);
      if (!hasOverlay) return undefined;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (event) => {
        if (event.key !== 'Escape') return;
        if (saving) return;
        if (modal) {
          closeModal();
          return;
        }
        if (selectedRow) closeDrawer();
      };
      window.addEventListener('keydown', onKey);
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = previousOverflow;
      };
    }, [selectedRow, modal, saving]);

    useEffect(() => {
      if (!selectedRow) return undefined;
      if (drawerCloseButtonRef.current) drawerCloseButtonRef.current.focus();
      return undefined;
    }, [selectedRow]);

    useEffect(() => {
      if (!modal) return undefined;
      const timer = setTimeout(() => {
        let target = null;
        if (modal === 'create') {
          target = document.getElementById('billing-subscription-tenant-search');
        } else if (modal === 'edit') {
          target = document.getElementById('billing-subscription-amount');
        } else if (modal === 'end' || modal === 'cancel') {
          target = document.getElementById('billing-subscription-reason');
        }
        if (target && typeof target.focus === 'function') {
          target.focus();
        } else if (modalInitialFocusRef.current && typeof modalInitialFocusRef.current.focus === 'function') {
          modalInitialFocusRef.current.focus();
        }
      }, 0);
      return () => clearTimeout(timer);
    }, [modal]);

    useEffect(() => {
      if (modal || !selectedRow) return undefined;
      const onKey = (event) => {
        trapFocus(event, drawerDialogRef.current);
      };
      const node = drawerDialogRef.current;
      if (node) node.addEventListener('keydown', onKey);
      return () => {
        if (node) node.removeEventListener('keydown', onKey);
      };
    }, [modal, selectedRow]);

    useEffect(() => {
      if (!modal) return undefined;
      const onKey = (event) => {
        trapFocus(event, modalDialogRef.current);
      };
      const node = modalDialogRef.current;
      if (node) node.addEventListener('keydown', onKey);
      return () => {
        if (node) node.removeEventListener('keydown', onKey);
      };
    }, [modal]);

    const handleDrawerTabKeyDown = (event) => {
      const tabs = ['detalhes', 'historico'];
      const handledKeys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (!handledKeys.includes(event.key)) return;
      event.preventDefault();
      const currentIndex = tabs.indexOf(drawerTab);
      if (currentIndex < 0) return;
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      }
      const nextTab = tabs[nextIndex];
      setDrawerTab(nextTab);
      const tabEl = drawerTabRefs.current[nextTab];
      if (tabEl && typeof tabEl.focus === 'function') tabEl.focus();
    };

    useEffect(() => {
      if (modal !== 'create') return undefined;
      const trimmed = tenantSearch.trim();
      if (!trimmed || selectedTenant) {
        setTenantResults([]);
        setTenantLoading(false);
        return undefined;
      }
      const requestId = ++tenantSearchRef.current;
      const timer = setTimeout(async () => {
        setTenantLoading(true);
        try {
          const service = tenantService;
          if (!service || typeof service.list !== 'function') {
            setTenantResults([]);
            return;
          }
          const result = await service.list({ search: trimmed, page: 1, page_size: 20 });
          if (requestId !== tenantSearchRef.current) return;
          setTenantResults(Array.isArray(result.data) ? result.data : []);
        } catch (_err) {
          if (requestId !== tenantSearchRef.current) return;
          setTenantResults([]);
        } finally {
          if (requestId === tenantSearchRef.current) setTenantLoading(false);
        }
      }, 350);
      return () => {
        clearTimeout(timer);
        tenantSearchRef.current += 1;
      };
    }, [tenantSearch, modal, selectedTenant]);

    const setFilterValue = (key, value) => {
      setListState((prev) => Object.assign({}, prev, {
        [key]: value,
        page: key === 'page' ? Math.max(1, Number(value) || 1) : 1,
      }));
    };

    const clearFilters = () => {
      setSearchInput('');
      setListState((prev) => Object.assign({}, DEFAULT_LIST_STATE, {
        page_size: prev.page_size,
      }));
    };

    const loadDetail = async (row) => {
      if (!row) return;
      const requestId = ++detailRequestRef.current;
      const entityKey = billingEntityKey(row.tenant_id, row.id);
      setDetailLoading(true);
      setDetailError(null);
      try {
        const detailResult = await billingService.getTenantSubscriptionDetail(
          row.tenant_id,
          row.id,
        );
        if (requestId !== detailRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        setDetail(detailResult);
      } catch (err) {
        if (requestId !== detailRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        setDetailError(formatError(err, 'Não foi possível carregar o detalhe da assinatura.'));
      } finally {
        if (requestId === detailRequestRef.current) setDetailLoading(false);
      }
    };

    const loadInitialHistory = async (row) => {
      if (!row) return;
      const requestId = ++historyRequestRef.current;
      const entityKey = billingEntityKey(row.tenant_id, row.id);
      setHistoryLoading(true);
      setHistoryError(null);
      setHistoryItems([]);
      setHistoryMeta({ count: 0, page: 1, page_size: 20 });
      try {
        const historyResult = await billingService.getTenantSubscriptionHistory(
          row.tenant_id,
          row.id,
          { page: 1, page_size: 20 },
        );
        if (requestId !== historyRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        setHistoryItems(Array.isArray(historyResult.results) ? historyResult.results : []);
        setHistoryMeta({
          count: Number(historyResult.count) || 0,
          page: Number(historyResult.page) || 1,
          page_size: Number(historyResult.page_size) || 20,
        });
      } catch (err) {
        if (requestId !== historyRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        setHistoryError(formatError(err, 'Não foi possível carregar o histórico.'));
      } finally {
        if (requestId === historyRequestRef.current) setHistoryLoading(false);
      }
    };

    const loadDetailAndHistory = (row) => {
      if (!row) return;
      setDrawerTab('detalhes');
      setHistoryExpanded({});
      loadDetail(row);
      loadInitialHistory(row);
    };

    const openDrawer = (row, event) => {
      if (event && event.currentTarget) drawerTriggerRef.current = event.currentTarget;
      detailRequestRef.current += 1;
      historyRequestRef.current += 1;
      activeEntityKeyRef.current = billingEntityKey(row.tenant_id, row.id);
      setSelectedRow(row);
      setDetail(null);
      setDetailError(null);
      setHistoryItems([]);
      setHistoryMeta({ count: 0, page: 1, page_size: 20 });
      setHistoryError(null);
      setHistoryExpanded({});
      setDrawerTab('detalhes');
      loadDetailAndHistory(row);
    };

    const closeDrawer = () => {
      if (saving) return;
      detailRequestRef.current += 1;
      historyRequestRef.current += 1;
      activeEntityKeyRef.current = null;
      setSelectedRow(null);
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      setHistoryItems([]);
      setHistoryMeta({ count: 0, page: 1, page_size: 20 });
      setHistoryError(null);
      setHistoryLoading(false);
      setHistoryExpanded({});
      setDrawerTab('detalhes');
      setModal(null);
      setForm(null);
      setFormErrors({});
      setFormError(null);
      tenantSearchRef.current += 1;
      setSelectedTenant(null);
      setTenantSearch('');
      setTenantResults([]);
      setTenantLoading(false);
      if (drawerTriggerRef.current && typeof drawerTriggerRef.current.focus === 'function') {
        drawerTriggerRef.current.focus();
      }
    };

    const loadMoreHistory = async () => {
      if (!selectedRow || historyLoading) return;
      const entityKey = billingEntityKey(selectedRow.tenant_id, selectedRow.id);
      const nextPage = (historyMeta.page || 1) + 1;
      const requestId = ++historyRequestRef.current;
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const historyResult = await billingService.getTenantSubscriptionHistory(
          selectedRow.tenant_id,
          selectedRow.id,
          { page: nextPage, page_size: historyMeta.page_size || 20 },
        );
        if (requestId !== historyRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        const incoming = Array.isArray(historyResult.results) ? historyResult.results : [];
        setHistoryItems((prev) => {
          const seen = {};
          prev.forEach((item) => { seen[item.id] = true; });
          return prev.concat(incoming.filter((item) => !seen[item.id]));
        });
        setHistoryMeta({
          count: Number(historyResult.count) || 0,
          page: Number(historyResult.page) || nextPage,
          page_size: Number(historyResult.page_size) || 20,
        });
      } catch (err) {
        if (requestId !== historyRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        setHistoryError(formatError(err, 'Não foi possível carregar o histórico.'));
      } finally {
        if (requestId === historyRequestRef.current) setHistoryLoading(false);
      }
    };

    const openCreateModal = (event) => {
      if (event && event.currentTarget) modalTriggerRef.current = event.currentTarget;
      tenantSearchRef.current += 1;
      setTenantLoading(false);
      setModal('create');
      setSelectedTenant(null);
      setTenantSearch('');
      setTenantResults([]);
      setForm({
        amount: '',
        billing_cycle: 'monthly',
        start_date: todayLocalISO(),
        next_due_date: '',
        payment_method: 'not_informed',
        notes: '',
        reason: '',
      });
      setFormErrors({});
      setFormError(null);
    };

    const openEditModal = (event) => {
      if (!canManage || !detail || detail.status !== 'active') return;
      if (event && event.currentTarget) modalTriggerRef.current = event.currentTarget;
      setModal('edit');
      setForm({
        amount: detail.amount != null ? String(detail.amount) : '',
        billing_cycle: detail.billing_cycle || 'monthly',
        start_date: detail.start_date || '',
        next_due_date: detail.next_due_date || '',
        payment_method: detail.payment_method || 'not_informed',
        notes: detail.notes || '',
        reason: '',
      });
      setFormErrors({});
      setFormError(null);
    };

    const openEndModal = (event) => {
      if (!canManage || !detail || detail.status !== 'active') return;
      if (event && event.currentTarget) modalTriggerRef.current = event.currentTarget;
      setModal('end');
      setForm({ reason: '' });
      setFormErrors({});
      setFormError(null);
    };

    const openCancelModal = (event) => {
      if (!canManage || !detail || detail.status !== 'active') return;
      if (event && event.currentTarget) modalTriggerRef.current = event.currentTarget;
      setModal('cancel');
      setForm({ reason: '' });
      setFormErrors({});
      setFormError(null);
    };

    const closeModal = () => {
      if (saving) return;
      tenantSearchRef.current += 1;
      setTenantLoading(false);
      setModal(null);
      setForm(null);
      setFormErrors({});
      setFormError(null);
      setSelectedTenant(null);
      setTenantSearch('');
      setTenantResults([]);
      if (modalTriggerRef.current && typeof modalTriggerRef.current.focus === 'function') {
        modalTriggerRef.current.focus();
      }
    };

    const selectTenant = (tenant) => {
      tenantSearchRef.current += 1;
      setTenantLoading(false);
      setSelectedTenant(tenant);
      setTenantSearch('');
      setTenantResults([]);
    };

    const clearSelectedTenant = () => {
      tenantSearchRef.current += 1;
      setTenantLoading(false);
      setSelectedTenant(null);
      setTenantResults([]);
    };

    const REFRESH_PARTIAL_WARNING = 'Operação concluída, mas não foi possível atualizar todos os dados da tela.';

    const refreshAfterWrite = async (subscriptionPayload, rowOverride) => {
      if (subscriptionPayload) setDetail(subscriptionPayload);
      const row = rowOverride || selectedRow;
      if (row) {
        const entityKey = billingEntityKey(row.tenant_id, row.id);
        const requestId = ++historyRequestRef.current;
        try {
          const historyResult = await billingService.getTenantSubscriptionHistory(
            row.tenant_id,
            row.id,
            { page: 1, page_size: 20 },
          );
          const canApplyHistory =
            requestId === historyRequestRef.current
            && activeEntityKeyRef.current === entityKey;
          if (canApplyHistory) {
            setHistoryItems(Array.isArray(historyResult.results) ? historyResult.results : []);
            setHistoryMeta({
              count: Number(historyResult.count) || 0,
              page: Number(historyResult.page) || 1,
              page_size: Number(historyResult.page_size) || 20,
            });
            setHistoryError(null);
          }
        } catch (_err) {
          // Histórico é best-effort.
        }
      }

      let refreshWarning = null;
      if (typeof refetch === 'function') {
        try {
          const listRefresh = await refetch();
          if (
            listRefresh
            && listRefresh.ok === false
            && listRefresh.stale !== true
          ) {
            refreshWarning = REFRESH_PARTIAL_WARNING;
          }
        } catch (_err) {
          refreshWarning = REFRESH_PARTIAL_WARNING;
        }
      }
      if (typeof onBillingChanged === 'function') {
        try {
          const overviewRefresh = await Promise.resolve(
            onBillingChanged(),
          );
          if (
            overviewRefresh
            && overviewRefresh.ok === false
            && overviewRefresh.stale !== true
          ) {
            refreshWarning = REFRESH_PARTIAL_WARNING;
          }
        } catch (_err) {
          if (!refreshWarning) refreshWarning = REFRESH_PARTIAL_WARNING;
        }
      }
      return refreshWarning;
    };

    const isSubscriptionModalSubmitDisabled = () => {
      if (modal === 'create') return !canManage;
      if (modal === 'edit' || modal === 'end' || modal === 'cancel') {
        return !canManage || !detail || detail.status !== 'active';
      }
      return false;
    };

    const handleCreate = async () => {
      if (!canManage) return;
      const validation = saBillingValidateSubscriptionForm(
        Object.assign({}, form, { tenant_id: selectedTenant && selectedTenant.tenant_id }),
        'create',
      );
      setFormErrors(validation.errors);
      if (!validation.valid) {
        focusFirstFormError(validation.errors);
        return;
      }
      if (!selectedTenant || !isPositiveIntegerId(selectedTenant.tenant_id)) {
        const tenantErrors = Object.assign({}, validation.errors, { tenant_id: 'Selecione um tenant.' });
        setFormErrors(tenantErrors);
        focusFirstFormError(tenantErrors);
        return;
      }
      setSaving(true);
      setFormError(null);
      try {
        const result = await billingService.createTenantSubscription(
          selectedTenant.tenant_id,
          validation.payload,
        );
        onToast && onToast('Assinatura criada com sucesso.');
        closeModal();
        drawerTriggerRef.current = modalTriggerRef.current;
        if (result && result.subscription) {
          const row = Object.assign({}, result.subscription, {
            tenant_id: selectedTenant.tenant_id,
            tenant_name: selectedTenant.name,
            tenant_schema: selectedTenant.schema_name,
          });
          setDrawerTab('detalhes');
          setHistoryExpanded({});
          setSelectedRow(row);
          activeEntityKeyRef.current = billingEntityKey(selectedTenant.tenant_id, result.subscription.id);
          const refreshWarning = await refreshAfterWrite(result.subscription, row);
          if (refreshWarning && onToast) onToast(refreshWarning);
        } else {
          const refreshWarning = await refreshAfterWrite(null);
          if (refreshWarning && onToast) onToast(refreshWarning);
        }
      } catch (err) {
        setFormError(formatError(err, 'Não foi possível criar a assinatura.'));
      } finally {
        setSaving(false);
      }
    };

    const handleEdit = async () => {
      if (!canManage) return;
      if (!selectedRow || !detail) return;
      if (detail.status !== 'active') {
        setFormError('A assinatura não está mais ativa. Atualize os dados antes de continuar.');
        await loadDetail(selectedRow);
        return;
      }
      const validation = saBillingValidateSubscriptionForm(form, 'edit');
      setFormErrors(validation.errors);
      if (!validation.valid) {
        focusFirstFormError(validation.errors);
        return;
      }
      setSaving(true);
      setFormError(null);
      try {
        const result = await billingService.updateTenantSubscription(
          selectedRow.tenant_id,
          selectedRow.id,
          validation.payload,
        );
        if (result && result.noop === true) {
          onToast && onToast('Nenhuma alteração foi necessária.');
        } else {
          onToast && onToast('Assinatura atualizada com sucesso.');
        }
        closeModal();
        const refreshWarning = await refreshAfterWrite(result && result.subscription);
        if (refreshWarning && onToast) onToast(refreshWarning);
      } catch (err) {
        const message = formatError(err, 'Não foi possível atualizar a assinatura.');
        setFormError(message);
        if (err && err.status === 409) {
          try {
            const refreshed = await billingService.getTenantSubscriptionDetail(
              selectedRow.tenant_id,
              selectedRow.id,
            );
            setDetail(refreshed);
          } catch (_refreshErr) {
            // keep previous detail
          }
        }
      } finally {
        setSaving(false);
      }
    };

    const handleEndOrCancel = async (action) => {
      if (!canManage) return;
      if (!selectedRow || !detail) return;
      if (action !== 'end' && action !== 'cancel') return;
      if (detail.status !== 'active') {
        setFormError('A assinatura não está mais ativa. Atualize os dados antes de continuar.');
        await loadDetail(selectedRow);
        return;
      }
      const reason = form && form.reason != null ? String(form.reason).trim() : '';
      const errors = {};
      if (reason.length < 5 || reason.length > 500) {
        errors.reason = 'Motivo deve ter entre 5 e 500 caracteres.';
      }
      setFormErrors(errors);
      if (Object.keys(errors).length) {
        focusFirstFormError(errors);
        return;
      }

      setSaving(true);
      setFormError(null);
      try {
        const fn = action === 'end'
          ? billingService.endTenantSubscription
          : billingService.cancelTenantSubscription;
        const result = await fn(selectedRow.tenant_id, selectedRow.id, { reason });
        onToast && onToast(
          action === 'end'
            ? 'Assinatura encerrada com sucesso.'
            : 'Assinatura cancelada com sucesso.',
        );
        closeModal();
        const refreshWarning = await refreshAfterWrite(result && result.subscription);
        if (refreshWarning && onToast) onToast(refreshWarning);
      } catch (err) {
        const message = formatError(
          err,
          action === 'end'
            ? 'Não foi possível encerrar a assinatura.'
            : 'Não foi possível cancelar a assinatura.',
        );
        setFormError(message);
        if (err && err.status === 409) {
          try {
            const refreshed = await billingService.getTenantSubscriptionDetail(
              selectedRow.tenant_id,
              selectedRow.id,
            );
            setDetail(refreshed);
          } catch (_refreshErr) {
            // keep previous detail
          }
        }
      } finally {
        setSaving(false);
      }
    };

    const isInitialLoading = loading && (!data || data.length === 0) && !error;
    const showStaleError = Boolean(error && data && data.length > 0);
    const showEmpty = !loading && !error && !filtersInvalid && vm.is_empty;

    const statusBadgeClass = (status) => {
      if (status === 'active') return 'badge success';
      if (status === 'ended') return 'badge neutral';
      if (status === 'cancelled') return 'badge danger';
      return 'badge neutral';
    };

    return (
      <div className="billing-subscriptions">
        <div className="billing-subscriptions-header">
          <div>
            <h2>Assinaturas</h2>
            <p>Assinaturas internas cadastradas para os tenants da plataforma.</p>
            <div className="billing-subscriptions-count">
              {meta.count} registro{meta.count === 1 ? '' : 's'}
            </div>
          </div>
          <div className="billing-subscriptions-actions">
            <button
              type="button"
              className="btn"
              onClick={() => refetch && refetch()}
              disabled={loading}
              aria-busy={loading || undefined}
            >
              {loading ? 'Atualizando…' : 'Atualizar'}
            </button>
            {canManage && (
              <button type="button" className="btn primary" onClick={openCreateModal}>
                Nova assinatura
              </button>
            )}
          </div>
        </div>

        <div className="billing-subscriptions-toolbar">
          <div className="billing-subscriptions-search">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value.slice(0, 200))}
              placeholder="Buscar por tenant, schema ou plano"
              maxLength={200}
              aria-label="Buscar por tenant, schema ou plano"
            />
            {searchInput && (
              <button type="button" className="btn" onClick={() => setSearchInput('')}>
                Limpar
              </button>
            )}
          </div>

          <div className="billing-subscriptions-filters">
            <label>
              <span>Status da assinatura</span>
              <select
                value={listState.status}
                onChange={(e) => setFilterValue('status', e.target.value)}
              >
                <option value="">Todos</option>
                {SUBSCRIPTION_STATUSES.map((status) => (
                  <option key={status} value={status}>{formatStatus(status)}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Status do tenant</span>
              <select
                value={listState.tenant_status}
                onChange={(e) => setFilterValue('tenant_status', e.target.value)}
              >
                <option value="">Todos</option>
                {TENANT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {TENANT_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Ciclo</span>
              <select
                value={listState.billing_cycle}
                onChange={(e) => setFilterValue('billing_cycle', e.target.value)}
              >
                <option value="">Todos</option>
                {BILLING_CYCLES.map((cycle) => (
                  <option key={cycle} value={cycle}>{formatCycle(cycle)}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Forma de pagamento</span>
              <select
                value={listState.payment_method}
                onChange={(e) => setFilterValue('payment_method', e.target.value)}
              >
                <option value="">Todas</option>
                {PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>{formatPayment(method)}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Plano</span>
              <input
                type="text"
                value={listState.plan_code}
                onChange={(e) => setFilterValue('plan_code', e.target.value)}
                placeholder="Código do plano"
              />
            </label>

            <label>
              <span>Ordenação</span>
              <select
                value={listState.ordering}
                onChange={(e) => setFilterValue('ordering', e.target.value)}
              >
                {ORDERING_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="billing-subscriptions-advanced-toggle">
            <button
              type="button"
              className="btn"
              onClick={() => setShowAdvanced((prev) => !prev)}
              aria-expanded={showAdvanced}
              aria-controls="billing-subscriptions-advanced-filters"
            >
              {showAdvanced ? 'Ocultar mais filtros' : 'Mais filtros'}
            </button>
            <button type="button" className="btn" onClick={clearFilters}>
              Limpar filtros
            </button>
          </div>

          {showAdvanced && (
            <div id="billing-subscriptions-advanced-filters" className="billing-subscriptions-advanced">
              <label>
                <span>Próximo vencimento de</span>
                <input
                  type="date"
                  value={listState.next_due_date_from}
                  onChange={(e) => setFilterValue('next_due_date_from', e.target.value)}
                />
              </label>
              <label>
                <span>Próximo vencimento até</span>
                <input
                  type="date"
                  value={listState.next_due_date_to}
                  onChange={(e) => setFilterValue('next_due_date_to', e.target.value)}
                />
              </label>
              <label>
                <span>Criada de</span>
                <input
                  type="date"
                  value={listState.created_from}
                  onChange={(e) => setFilterValue('created_from', e.target.value)}
                />
              </label>
              <label>
                <span>Criada até</span>
                <input
                  type="date"
                  value={listState.created_to}
                  onChange={(e) => setFilterValue('created_to', e.target.value)}
                />
              </label>
              {(dueRangeError || createdRangeError) && (
                <div className="billing-subscriptions-alert" role="alert">
                  {dueRangeError || createdRangeError}
                </div>
              )}
            </div>
          )}
        </div>

        {showStaleError && (
          <div className="billing-subscriptions-alert" role="alert">
            <span>{error}</span>
            <button type="button" className="btn" onClick={() => refetch && refetch()}>
              Tentar novamente
            </button>
          </div>
        )}

        {filtersInvalid && (
          <div className="billing-subscriptions-alert" role="alert">
            Corrija o intervalo de datas antes de consultar a lista.
          </div>
        )}

        {isInitialLoading && (
          <div
            className="billing-subscriptions-skeleton"
            aria-busy="true"
            aria-live="polite"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="billing-subscriptions-skel-row" />
            ))}
          </div>
        )}

        {!isInitialLoading && error && (!data || data.length === 0) && (
          typeof ApiErrorState === 'function'
            ? <ApiErrorState error={error} onRetry={refetch} />
            : (
              <div className="billing-subscriptions-alert" role="alert">
                <span>{error}</span>
                <button type="button" className="btn" onClick={() => refetch && refetch()}>
                  Tentar novamente
                </button>
              </div>
            )
        )}

        {showEmpty && !hasActiveFilters && (
          <div className="hint-card billing-subscriptions-empty">
            <div className="billing-subscriptions-empty-title">
              Nenhuma assinatura interna cadastrada
            </div>
            <p>As assinaturas aparecerão aqui após o primeiro cadastro.</p>
            {canManage && (
              <button type="button" className="btn primary" onClick={openCreateModal}>
                Nova assinatura
              </button>
            )}
          </div>
        )}

        {showEmpty && hasActiveFilters && (
          <div className="hint-card billing-subscriptions-empty">
            <div className="billing-subscriptions-empty-title">
              Nenhuma assinatura encontrada
            </div>
            <button type="button" className="btn" onClick={clearFilters}>
              Limpar filtros
            </button>
          </div>
        )}

        {!isInitialLoading && !showEmpty && !(error && (!data || data.length === 0)) && !filtersInvalid && (
          <>
            <div className="billing-subscriptions-table-wrap">
              <table className="billing-subscriptions-table">
                <caption className="billing-sr-only">
                  Lista de assinaturas internas dos tenants
                </caption>
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Plano</th>
                    <th>Status</th>
                    <th>Valor</th>
                    <th>Ciclo</th>
                    <th>Equivalente mensal</th>
                    <th>Próximo vencimento</th>
                    <th>Pagamento</th>
                    <th>Status do tenant</th>
                    <th>Sincronização</th>
                    <th className="billing-subscriptions-col-updated">Atualização</th>
                  </tr>
                </thead>
                <tbody>
                  {vm.rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={(e) => openDrawer(row, e)}
                    >
                      <td>
                        <button
                          type="button"
                          className="billing-row-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDrawer(row, e);
                          }}
                          aria-label={`Abrir assinatura ${row.id} do tenant ${row.tenant_name || 'sem nome'}`}
                        >
                          <div className="billing-subscriptions-tenant-name">{row.tenant_name || '—'}</div>
                          <div className="billing-subscriptions-tenant-schema">{row.tenant_schema || '—'}</div>
                        </button>
                      </td>
                      <td>
                        <div>{row.plan_label}</div>
                        {row.plan_code_label && (
                          <div className="billing-subscriptions-muted">{row.plan_code_label}</div>
                        )}
                      </td>
                      <td>
                        <span className={statusBadgeClass(row.status)}>
                          <span className="dot" />
                          {row.status_label}
                        </span>
                      </td>
                      <td>{row.amount_formatted}</td>
                      <td>{row.billing_cycle_label}</td>
                      <td>{row.monthly_equivalent_formatted}</td>
                      <td>{row.next_due_date_formatted}</td>
                      <td>{row.payment_method_label}</td>
                      <td>{row.tenant_status_label}</td>
                      <td>
                        <span className={row.sync_tone === 'warning' ? 'badge warning' : 'badge success'}>
                          <span className="dot" />
                          {row.sync_label}
                        </span>
                      </td>
                      <td className="billing-subscriptions-col-updated">{row.updated_at_formatted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="billing-subscriptions-mobile-list">
              {vm.rows.map((row) => (
                <button
                  key={`m-${row.id}`}
                  type="button"
                  className="billing-subscriptions-mobile-card"
                  onClick={(e) => openDrawer(row, e)}
                  aria-label={`Assinatura ${row.id}, tenant ${row.tenant_name || 'sem nome'}, plano ${row.plan_label}, status ${row.status_label}, valor ${row.amount_formatted}, vencimento ${row.next_due_date_formatted}`}
                >
                  <div className="billing-subscriptions-mobile-top">
                    <strong>{row.tenant_name || '—'}</strong>
                    <span className={statusBadgeClass(row.status)}>
                      <span className="dot" />
                      {row.status_label}
                    </span>
                  </div>
                  <div className="billing-subscriptions-muted">{row.tenant_schema}</div>
                  <div>{row.plan_label} · {row.amount_formatted}</div>
                  <div className="billing-subscriptions-muted">
                    {row.billing_cycle_label} · vence {row.next_due_date_formatted}
                  </div>
                </button>
              ))}
            </div>

            <nav className="billing-subscriptions-pagination" aria-label="Paginação de assinaturas">
              <div aria-live="polite" aria-atomic="true">
                {meta.count} registro{meta.count === 1 ? '' : 's'} · Página {meta.page} de {meta.total_pages}
              </div>
              <div className="billing-subscriptions-pagination-controls">
                <button
                  type="button"
                  className="btn"
                  disabled={meta.page <= 1 || loading}
                  onClick={() => setFilterValue('page', meta.page - 1)}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={meta.page >= meta.total_pages || loading}
                  onClick={() => setFilterValue('page', meta.page + 1)}
                >
                  Próxima
                </button>
                <select
                  value={listState.page_size}
                  onChange={(e) => setFilterValue('page_size', Number(e.target.value))}
                  aria-label="Itens por página"
                >
                  {[20, 50, 100].map((size) => (
                    <option key={size} value={size}>{size} / página</option>
                  ))}
                </select>
              </div>
            </nav>
          </>
        )}

        {selectedRow && (
          <div
            className="billing-subscription-drawer-backdrop"
            onClick={() => { if (!saving) closeDrawer(); }}
          >
            <div
              ref={drawerDialogRef}
              className="billing-subscription-drawer"
              role="dialog"
              aria-modal={modal ? undefined : 'true'}
              aria-hidden={modal ? 'true' : undefined}
              aria-labelledby="billing-subscription-drawer-title"
              style={modal ? { pointerEvents: 'none' } : undefined}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="billing-subscription-drawer-header">
                <div>
                  <h3 id="billing-subscription-drawer-title">Assinatura #{selectedRow.id}</h3>
                  <div className="billing-subscriptions-muted">
                    {selectedRow.tenant_name || '—'}
                    {selectedRow.tenant_schema ? ` · ${selectedRow.tenant_schema}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn"
                  ref={drawerCloseButtonRef}
                  aria-label="Fechar detalhe da assinatura"
                  onClick={closeDrawer}
                  disabled={saving}
                >
                  Fechar
                </button>
              </div>

              <div
                className="billing-subscription-drawer-tabs"
                role="tablist"
                aria-label="Seções da assinatura"
                onKeyDown={handleDrawerTabKeyDown}
              >
                <button
                  type="button"
                  ref={(el) => { drawerTabRefs.current.detalhes = el; }}
                  id="billing-subscription-detail-tab"
                  role="tab"
                  className={drawerTab === 'detalhes' ? 'is-active' : ''}
                  aria-selected={drawerTab === 'detalhes'}
                  aria-controls="billing-subscription-detail-panel"
                  tabIndex={drawerTab === 'detalhes' ? 0 : -1}
                  onClick={() => setDrawerTab('detalhes')}
                >
                  Detalhes
                </button>
                <button
                  type="button"
                  ref={(el) => { drawerTabRefs.current.historico = el; }}
                  id="billing-subscription-history-tab"
                  role="tab"
                  className={drawerTab === 'historico' ? 'is-active' : ''}
                  aria-selected={drawerTab === 'historico'}
                  aria-controls="billing-subscription-history-panel"
                  tabIndex={drawerTab === 'historico' ? 0 : -1}
                  onClick={() => setDrawerTab('historico')}
                >
                  Histórico
                </button>
              </div>

              <div className="billing-subscription-drawer-body">
                {drawerTab === 'detalhes' && (
                  <div
                    id="billing-subscription-detail-panel"
                    role="tabpanel"
                    aria-labelledby="billing-subscription-detail-tab"
                    tabIndex={0}
                  >
                    {detailLoading && (
                      <div aria-busy="true" aria-live="polite">Carregando detalhe…</div>
                    )}
                    {detailError && (
                      <div className="billing-subscriptions-alert" role="alert">
                        <span>{detailError}</span>
                        <button type="button" className="btn" onClick={() => loadDetail(selectedRow)}>
                          Tentar novamente
                        </button>
                      </div>
                    )}
                    {!detailLoading && !detailError && detail && (
                      <>
                        <div className="billing-subscription-detail-grid">
                          <DetailField label="Tenant ID" value={detail.tenant_id} />
                          <DetailField label="Status" value={formatStatus(detail.status)} />
                          <DetailField label="Plano (snapshot)" value={detail.plan_name_snapshot || detail.plan_code_snapshot} />
                          <DetailField label="Código do plano (snapshot)" value={detail.plan_code_snapshot} />
                          <DetailField label="Plano operacional" value={detail.operational_plan} />
                          <DetailField
                            label="Sincronização"
                            value={detail.plan_in_sync === true ? 'Sincronizado' : 'Divergente'}
                          />
                          <DetailField label="Valor" value={money(detail.amount)} />
                          <DetailField label="Moeda" value={detail.currency} />
                          <DetailField label="Ciclo" value={formatCycle(detail.billing_cycle)} />
                          <DetailField label="Início" value={dateBR(detail.start_date)} />
                          <DetailField label="Próximo vencimento" value={dateBR(detail.next_due_date)} />
                          <DetailField label="Pagamento" value={formatPayment(detail.payment_method)} />
                          <DetailField label="Observações" value={detail.notes} />
                          <DetailField label="Criada em" value={dateTimeBR(detail.created_at)} />
                          <DetailField label="Atualizada em" value={dateTimeBR(detail.updated_at)} />
                          <DetailField label="Criada por" value={actorLabel(detail.created_by)} />
                          <DetailField label="Atualizada por" value={actorLabel(detail.updated_by)} />
                          <DetailField label="Encerrada em" value={dateTimeBR(detail.ended_at)} />
                          <DetailField label="Encerrada por" value={actorLabel(detail.ended_by)} />
                          <DetailField label="Motivo do encerramento" value={detail.ended_reason} />
                          <DetailField label="Cancelada em" value={dateTimeBR(detail.cancelled_at)} />
                          <DetailField label="Cancelada por" value={actorLabel(detail.cancelled_by)} />
                          <DetailField label="Motivo do cancelamento" value={detail.cancelled_reason} />
                        </div>

                        {canManage && detail.status === 'active' && (
                          <div className="billing-subscription-drawer-actions">
                            <button type="button" className="btn" onClick={openEditModal}>
                              Editar assinatura
                            </button>
                            <button type="button" className="btn" onClick={openEndModal}>
                              Encerrar assinatura
                            </button>
                            <button type="button" className="btn danger" onClick={openCancelModal}>
                              Cancelar assinatura
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {drawerTab === 'historico' && (
                  <div
                    id="billing-subscription-history-panel"
                    role="tabpanel"
                    aria-labelledby="billing-subscription-history-tab"
                    tabIndex={0}
                    className="billing-subscription-history"
                  >
                    {historyLoading && historyItems.length === 0 && !historyError && (
                      <div aria-busy="true" aria-live="polite">Carregando histórico…</div>
                    )}
                    {historyError && (
                      <div className="billing-subscriptions-alert" role="alert">
                        <span>{historyError}</span>
                        <button type="button" className="btn" onClick={() => loadInitialHistory(selectedRow)}>
                          Tentar novamente
                        </button>
                      </div>
                    )}
                    {historyItems.length === 0 && !historyLoading && !historyError && (
                      <div className="billing-subscriptions-muted">Nenhum evento registrado.</div>
                    )}
                    {historyItems.map((event) => {
                      const diffs = snapshotDiffEntries(event.before, event.after);
                      const expanded = historyExpanded[event.id];
                      return (
                        <div key={event.id} className="billing-subscription-history-item">
                          <div className="billing-subscription-history-main">
                            <strong>
                              {HISTORY_EVENT_LABELS[event.event_type] || event.event_type}
                            </strong>
                            <span>{dateTimeBR(event.created_at)}</span>
                          </div>
                          <div className="billing-subscriptions-muted">
                            Ator: {actorLabel(event.actor)}
                          </div>
                          {event.reason && (
                            <div>Motivo: {event.reason}</div>
                          )}
                          {diffs.length > 0 && (
                            <div>
                              <button
                                type="button"
                                className="btn"
                                onClick={() => setHistoryExpanded((prev) => Object.assign({}, prev, {
                                  [event.id]: !prev[event.id],
                                }))}
                              >
                                {expanded ? 'Ocultar alterações' : 'Ver alterações'}
                              </button>
                              {expanded && (
                                <ul className="billing-subscription-history-diff">
                                  {diffs.map((diff) => (
                                    <li key={diff.key}>
                                      <code>{diff.key}</code>: {diff.before} → {diff.after}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {historyItems.length > 0 && historyItems.length < historyMeta.count && (
                      <button
                        type="button"
                        className="btn"
                        onClick={loadMoreHistory}
                        disabled={historyLoading}
                      >
                        {historyLoading ? 'Carregando…' : 'Carregar mais'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {modal && (
          <div
            className="billing-subscription-modal-backdrop"
            onClick={() => { if (!saving) closeModal(); }}
          >
            <div
              ref={modalDialogRef}
              className="billing-subscription-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby={
                modal === 'create' ? 'billing-subscription-modal-create-title'
                  : modal === 'edit' ? 'billing-subscription-modal-edit-title'
                    : modal === 'end' ? 'billing-subscription-modal-end-title'
                      : 'billing-subscription-modal-cancel-title'
              }
              onClick={(e) => e.stopPropagation()}
            >
              <div className="billing-subscription-modal-header">
                <h3
                  id={
                    modal === 'create' ? 'billing-subscription-modal-create-title'
                      : modal === 'edit' ? 'billing-subscription-modal-edit-title'
                        : modal === 'end' ? 'billing-subscription-modal-end-title'
                          : 'billing-subscription-modal-cancel-title'
                  }
                >
                  {modal === 'create' && 'Nova assinatura'}
                  {modal === 'edit' && 'Editar assinatura'}
                  {modal === 'end' && 'Encerrar assinatura'}
                  {modal === 'cancel' && 'Cancelar assinatura'}
                </h3>
                <button
                  type="button"
                  className="btn"
                  ref={modal === 'create' || modal === 'edit' ? modalInitialFocusRef : undefined}
                  onClick={closeModal}
                  disabled={saving}
                  aria-label={
                    modal === 'create' ? 'Fechar modal de criação da assinatura'
                      : modal === 'edit' ? 'Fechar modal de edição da assinatura'
                        : modal === 'end' ? 'Fechar modal de encerramento da assinatura'
                          : 'Fechar modal de cancelamento da assinatura'
                  }
                >
                  Fechar
                </button>
              </div>

              <div className="billing-subscription-modal-body">
                {modal === 'end' && (
                  <p>Use para finalizar normalmente a vigência da assinatura interna.</p>
                )}
                {modal === 'cancel' && (
                  <p>Use quando a assinatura deve ser cancelada administrativamente.</p>
                )}

                {(modal === 'create' || modal === 'edit') && form && (
                  <SubscriptionFormFields
                    form={form}
                    errors={formErrors}
                    onChange={(key, value) => setForm((prev) => Object.assign({}, prev, { [key]: value }))}
                    mode={modal === 'create' ? 'create' : 'edit'}
                    selectedTenant={selectedTenant}
                    tenantSearch={tenantSearch}
                    onTenantSearchChange={setTenantSearch}
                    tenantResults={tenantResults}
                    tenantLoading={tenantLoading}
                    onSelectTenant={selectTenant}
                    onClearTenant={clearSelectedTenant}
                  />
                )}

                {(modal === 'end' || modal === 'cancel') && form && (
                  <div className="billing-subscription-form-grid">
                    <div className="billing-subscription-form-span">
                      <label htmlFor="billing-subscription-reason">Motivo</label>
                      <textarea
                        id="billing-subscription-reason"
                        ref={modalInitialFocusRef}
                        rows={3}
                        value={form.reason}
                        onChange={(e) => setForm(Object.assign({}, form, { reason: e.target.value }))}
                        maxLength={500}
                        placeholder="Descreva o motivo (5 a 500 caracteres)"
                        aria-invalid={Boolean(formErrors.reason)}
                        aria-describedby={
                          formErrors.reason ? 'billing-subscription-reason-error' : undefined
                        }
                      />
                      <FieldError id="billing-subscription-reason-error" message={formErrors.reason} />
                    </div>
                  </div>
                )}

                {formError && (
                  <div className="billing-subscriptions-alert" role="alert">{formError}</div>
                )}
              </div>

              <div className="billing-subscription-modal-footer">
                <button type="button" className="btn" onClick={closeModal} disabled={saving}>
                  Fechar
                </button>
                {modal === 'create' && (
                  <button
                    type="button"
                    className="btn primary"
                    onClick={handleCreate}
                    disabled={saving || isSubscriptionModalSubmitDisabled()}
                  >
                    {saving ? 'Salvando…' : 'Criar assinatura'}
                  </button>
                )}
                {modal === 'edit' && (
                  <button
                    type="button"
                    className="btn primary"
                    onClick={handleEdit}
                    disabled={saving || isSubscriptionModalSubmitDisabled()}
                  >
                    {saving ? 'Salvando…' : 'Salvar alterações'}
                  </button>
                )}
                {modal === 'end' && (
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => handleEndOrCancel('end')}
                    disabled={saving || isSubscriptionModalSubmitDisabled()}
                  >
                    {saving ? 'Salvando…' : 'Encerrar assinatura'}
                  </button>
                )}
                {modal === 'cancel' && (
                  <button
                    type="button"
                    className="btn danger"
                    onClick={() => handleEndOrCancel('cancel')}
                    disabled={saving || isSubscriptionModalSubmitDisabled()}
                  >
                    {saving ? 'Salvando…' : 'Cancelar assinatura'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

export {
  SaBillingSubscriptions,
  saBillingBuildSubscriptionQuery,
  saBillingBuildSubscriptionsViewModel,
  saBillingValidateSubscriptionForm,
};
